/**
 * Typed selector hooks for the orders domain.
 *
 * Snapshot shape (from docs/ARCHITECTURE.md §3.3):
 *
 *   { as_of, mode,
 *     today: [ { order_id, symbol, side, quantity, price, strategy, status,
 *                slippage, margin_used, lot_size, lots, instrument_type,
 *                timestamp } ],   // newest-first, capped at MAX_ORDERS=500
 *     summary: { total_count, completed, rejected, open, total_qty,
 *                total_value } }
 */
import { useStateStore } from "@/state/store";
import { EMPTY_ARRAY } from "@/state/hooks/_empty";

export interface OrderRow {
  order_id:        string;
  symbol:          string;
  side:            string;            // "BUY" | "SELL"
  quantity:        number;
  price:           number;
  strategy:        string | null;
  status:          string;            // "COMPLETE" | "REJECTED" | ...
  slippage:        number;
  margin_used:     number;
  lot_size:        number;
  lots:            number;
  instrument_type: string;            // "EQUITY" | "OPT" | "FUT" | exch code (live)
  timestamp:       string | null;
}

export interface OrdersSummary {
  total_count: number;
  completed:   number;
  rejected:    number;
  open:        number;
  total_qty:   number;
  total_value: number;
}

export interface OrdersSnapshot {
  as_of:   string;
  mode:    "paper" | "live";
  today:   OrderRow[];
  summary: OrdersSummary;
  _seq?:   number;
}

export function useOrders(): OrdersSnapshot | null {
  return useStateStore((s) => s.orders.snapshot as OrdersSnapshot | null);
}

export function useOrdersList(): OrderRow[] {
  return useStateStore((s) => {
    const snap = s.orders.snapshot as OrdersSnapshot | null;
    return (snap?.today ?? EMPTY_ARRAY) as OrderRow[];
  });
}

export function useOrdersSummary(): OrdersSummary | null {
  return useStateStore((s) => {
    const snap = s.orders.snapshot as OrdersSnapshot | null;
    return snap?.summary ?? null;
  });
}

export function useOrdersIsStale(): boolean {
  return useStateStore((s) => s.orders.isStale);
}

export function useOrdersLastUpdated(): number {
  return useStateStore((s) => s.orders.lastUpdatedAt);
}
